import { getCashbackValue } from "./UserTools"
import { getDiscountOrder, getTotalOrder } from "./OrderTools"
import { getLocalItem } from "./StorageTools"

/**
 * Retorna o saldo de cashback disponível para o usuário,
 * já descontando valor usado em pedido anterior (localStorage).
 */
export const getAvailableCashbackValue = (user) => {
  let { value } = getCashbackValue(user?.movements_account || [])

  const cashbackUsed = getLocalItem("cashbackUsed")
  if (cashbackUsed) {
    const [, val_used] = cashbackUsed.split("__")
    value -= parseFloat(Number.parseFloat(val_used).toFixed(2))
  }

  return parseFloat(value.toFixed(2))
}

/**
 * Sincroniza o val_payment do cashback (e do método não-cashback) quando o total do pedido muda.
 * Se não houver cashback no array de pagamentos, retorna o order intacto.
 */
export const syncCashbackPayment = ({ order, user, settings, modality, userSelectedAddress }) => {
  const cashbackPayment = (order.payments || []).find((p) => p.nm_payment_method?.toLowerCase() === "cashback")
  if (!cashbackPayment) return order

  let value = getAvailableCashbackValue(user)

  const [total, subTotal, benefit] = getTotalOrder({ ...order, id_modality: modality, subTotal: order.total }, userSelectedAddress?.deliveryFee)
  const deliveryFee = modality !== 2 && modality !== 3 ? userSelectedAddress?.deliveryFee || 0 : 0
  const discount = getDiscountOrder(order, subTotal, deliveryFee, settings, modality)
  const totalOrder = total + deliveryFee - benefit - discount

  const cashbackPercentLimit = Number.parseFloat(settings?.cashback_order_percent_limit || 0)
  const cashbackMonetaryLimit = Number.parseFloat(settings?.cashback_payment_limit || 0)
  let effectiveLimit = Infinity
  if (cashbackMonetaryLimit > 0) effectiveLimit = Math.min(effectiveLimit, cashbackMonetaryLimit)
  if (cashbackPercentLimit > 0) effectiveLimit = Math.min(effectiveLimit, parseFloat((totalOrder * (cashbackPercentLimit / 100)).toFixed(2)))
  if (effectiveLimit < Infinity && value > effectiveLimit) value = effectiveLimit

  if (value > totalOrder) value = totalOrder

  const updatedCashback = { ...cashbackPayment, val_payment: value }
  const nonCashbackPayment = (order.payments || []).find((p) => p.nm_payment_method?.toLowerCase() !== "cashback")

  const newPayments = [updatedCashback]
  if (nonCashbackPayment) {
    newPayments.push({ ...nonCashbackPayment, val_payment: Math.max(0, totalOrder - value) })
  }

  return { ...order, payments: newPayments }
}

/**
 * Aplica ou remove o cashback do pedido.
 * Retorna o objeto `order` atualizado (sem mutar o original).
 *
 * @param {boolean} status - true para aplicar, false para remover
 * @param {object}  order
 * @param {object}  company
 * @param {object}  settings
 * @param {number}  modality
 * @param {object}  userSelectedAddress
 * @param {object}  user
 * @returns {object} order atualizado
 */
export const applyOrderCashback = ({ status, order, company, settings, modality, userSelectedAddress, user }) => {
  const updatedOrder = { ...order, payments: [...(order.payments || [])] }

  const { company_payment_methods = [] } = company

  let value = getAvailableCashbackValue(user)

  const [total, subTotal, benefit] = getTotalOrder(
    { ...updatedOrder, id_modality: modality, subTotal: updatedOrder.total },
    userSelectedAddress?.deliveryFee
  )
  const deliveryFee = modality !== 2 && modality !== 3 ? userSelectedAddress?.deliveryFee || 0 : 0
  const discount = getDiscountOrder(updatedOrder, subTotal, deliveryFee, settings, modality)
  const totalOrder = total + deliveryFee - benefit - discount

  const cashbackPercentLimit = Number.parseFloat(settings?.cashback_order_percent_limit || 0)
  const cashbackMonetaryLimit = Number.parseFloat(settings?.cashback_payment_limit || 0)
  let effectiveLimit = Infinity
  if (cashbackMonetaryLimit > 0) effectiveLimit = Math.min(effectiveLimit, cashbackMonetaryLimit)
  if (cashbackPercentLimit > 0) effectiveLimit = Math.min(effectiveLimit, parseFloat((totalOrder * (cashbackPercentLimit / 100)).toFixed(2)))
  if (effectiveLimit < Infinity && value > effectiveLimit) value = effectiveLimit

  if (status) {
    if (value > totalOrder) value = totalOrder

    if (updatedOrder.payments.length > 0) {
      updatedOrder.payments[0] = { ...updatedOrder.payments[0], val_payment: updatedOrder.payments[0].val_payment - value }
      if (updatedOrder.payments[0].val_payment <= 0) {
        updatedOrder.payments = []
      }
    }

    const cashbackPayment = company_payment_methods.find(({ nm_payment_method }) => nm_payment_method.toLowerCase() === "cashback")

    if (cashbackPayment) {
      const { category_association, ...rest } = cashbackPayment
      updatedOrder.payments.push({ ...rest, ...category_association, val_payment: value, val_receivable: 0 })
    }
  } else {
    const cashbackIndex = updatedOrder.payments.findIndex(({ nm_payment_method }) => nm_payment_method.toLowerCase() === "cashback")

    if (cashbackIndex !== -1) {
      updatedOrder.payments.splice(cashbackIndex, 1)
    }

    if (updatedOrder.payments.length > 0) {
      updatedOrder.payments[0] = { ...updatedOrder.payments[0], val_payment: totalOrder }
    }
  }

  return updatedOrder
}
